const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const META_PREFIX = '__rnr_ttl_session_e::';

const now = () => Date.now();

const canUseSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const isWebView = () =>
  typeof window !== 'undefined' && window.ReactNativeWebView != null;

const metaKeyFor = (key) => `${META_PREFIX}${key}`;

const toExpiresAt = (ttlMs) =>
  now() + (typeof ttlMs === 'number' && ttlMs > 0 ? ttlMs : DEFAULT_TTL_MS);

export function setSessionItemTtl(key, value, ttlMs = DEFAULT_TTL_MS) {
  if (!key) return;
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.setItem(key, String(value ?? ''));

    if (isWebView()) {
      window.sessionStorage.removeItem(metaKeyFor(key));
      return;
    }

    window.sessionStorage.setItem(metaKeyFor(key), String(toExpiresAt(ttlMs)));
  } catch {
    // ignore storage failures (private mode / quota / disabled)
  }
}

export function getSessionItemTtl(key, ttlMsForMigration = DEFAULT_TTL_MS) {
  if (!key) return null;
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return null;

    if (isWebView()) {
      return raw;
    }

    const mk = metaKeyFor(key);
    const meta = window.sessionStorage.getItem(mk);
    const expiresAt = meta != null ? parseInt(meta, 10) : NaN;

    if (Number.isFinite(expiresAt) && now() > expiresAt) {
      window.sessionStorage.removeItem(key);
      window.sessionStorage.removeItem(mk);
      return null;
    }

    if (!Number.isFinite(expiresAt)) {
      window.sessionStorage.setItem(mk, String(toExpiresAt(ttlMsForMigration)));
    }

    return raw;
  } catch {
    return null;
  }
}

export function removeSessionItem(key) {
  if (!key) return;
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(key);
    window.sessionStorage.removeItem(metaKeyFor(key));
  } catch {
    // ignore
  }
}

export function purgeExpiredSessionStorage(ttlMsForUntrackedKeys = DEFAULT_TTL_MS) {
  if (!canUseSessionStorage()) return;
  try {
    if (isWebView()) {
      const keys = [];
      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const k = window.sessionStorage.key(i);
        if (k) keys.push(k);
      }
      keys.forEach((k) => {
        if (k.startsWith(META_PREFIX)) window.sessionStorage.removeItem(k);
      });
      return;
    }

    const keys = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k) keys.push(k);
    }

    const nowMs = now();

    keys.forEach((k) => {
      if (k.startsWith(META_PREFIX)) {
        const originalKey = k.slice(META_PREFIX.length);
        const meta = window.sessionStorage.getItem(k);
        const expiresAt = meta != null ? parseInt(meta, 10) : NaN;
        if (!Number.isFinite(expiresAt)) {
          window.sessionStorage.removeItem(k);
          return;
        }
        if (nowMs > expiresAt) {
          window.sessionStorage.removeItem(originalKey);
          window.sessionStorage.removeItem(k);
        }
        return;
      }

      const mk = metaKeyFor(k);
      const meta = window.sessionStorage.getItem(mk);
      const expiresAt = meta != null ? parseInt(meta, 10) : NaN;
      if (!Number.isFinite(expiresAt)) {
        window.sessionStorage.setItem(mk, String(toExpiresAt(ttlMsForUntrackedKeys)));
      }
    });
  } catch {
    // ignore
  }
}

